import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "@/api/endpoints/notifications";
import type { NotificationListResult } from "../types";

// ── Query keys ────────────────────────────────────────────────────────────────
export const NOTIFICATION_KEYS = {
  all: ["notifications"] as const,
  list: (unreadOnly?: boolean) => ["notifications", "list", { unreadOnly }] as const,
  unreadCount: ["notifications", "unread-count"] as const,
};

// ── List (for the bell panel) ──────────────────────────────────────────────────
// The SSE stream pushes new rows (invalidates this query); the 60s refetch is a
// safety net for when the stream is between reconnects.
export function useNotifications(unreadOnly = false) {
  return useQuery<NotificationListResult>({
    queryKey: NOTIFICATION_KEYS.list(unreadOnly),
    queryFn: () => notificationsApi.list({ limit: 30, unreadOnly }),
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    // Don't pile onto the visibilitychange burst (the auth provider re-validates
    // the session on focus with a 5s timeout — a refetch storm there can starve
    // it and force a logout). The SSE stream + 60s poll keep this fresh anyway.
    refetchOnWindowFocus: false,
  });
}

// ── Unread count (for the bell badge) ──────────────────────────────────────────
export function useUnreadNotificationCount(): number {
  const query = useQuery({
    queryKey: NOTIFICATION_KEYS.unreadCount,
    queryFn: () => notificationsApi.unreadCount(),
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,   // see useNotifications — avoid the focus burst
  });
  return query.data ?? 0;
}

// ── Mutations ───────────────────────────────────────────────────────────────────
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    // Optimistic: flip the item to read + drop the unread count immediately.
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_KEYS.all });
      const prevLists = queryClient.getQueriesData<NotificationListResult>({
        queryKey: ["notifications", "list"],
      });
      const prevCount = queryClient.getQueryData<number>(NOTIFICATION_KEYS.unreadCount);
      const nowIso = new Date().toISOString();
      let wasUnread = false;
      queryClient.setQueriesData<NotificationListResult>(
        { queryKey: ["notifications", "list"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((n) => {
              if (n.id !== id || n.readAt) return n;
              wasUnread = true;
              return { ...n, readAt: nowIso };
            }),
          };
        },
      );
      if (wasUnread && typeof prevCount === "number") {
        queryClient.setQueryData<number>(NOTIFICATION_KEYS.unreadCount, Math.max(0, prevCount - 1));
      }
      return { prevLists, prevCount };
    },
    onError: (_e, _id, ctx) => {
      ctx?.prevLists?.forEach(([key, data]) => queryClient.setQueryData(key, data));
      if (ctx && typeof ctx.prevCount === "number") {
        queryClient.setQueryData(NOTIFICATION_KEYS.unreadCount, ctx.prevCount);
      }
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all }),
  });
}

export function useDismissNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    // Optimistically remove the item from every cached list + drop the unread
    // count immediately, so a single click makes it disappear (no wait for the
    // server round-trip / refetch). Rolls back on error, reconciles on settle.
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_KEYS.all });
      const prevLists = queryClient.getQueriesData<NotificationListResult>({
        queryKey: ["notifications", "list"],
      });
      const prevCount = queryClient.getQueryData<number>(NOTIFICATION_KEYS.unreadCount);
      let wasUnread = false;
      queryClient.setQueriesData<NotificationListResult>(
        { queryKey: ["notifications", "list"] },
        (old) => {
          if (!old) return old;
          const hit = old.items.find((n) => n.id === id);
          if (hit && !hit.readAt) wasUnread = true;
          return { ...old, items: old.items.filter((n) => n.id !== id) };
        },
      );
      if (wasUnread && typeof prevCount === "number") {
        queryClient.setQueryData<number>(NOTIFICATION_KEYS.unreadCount, Math.max(0, prevCount - 1));
      }
      return { prevLists, prevCount };
    },
    onError: (_err, _id, ctx) => {
      ctx?.prevLists?.forEach(([key, data]) => queryClient.setQueryData(key, data));
      if (ctx && typeof ctx.prevCount === "number") {
        queryClient.setQueryData(NOTIFICATION_KEYS.unreadCount, ctx.prevCount);
      }
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all }),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    // Optimistic: flip every cached item to read + zero the unread badge now.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_KEYS.all });
      const prevLists = queryClient.getQueriesData<NotificationListResult>({
        queryKey: ["notifications", "list"],
      });
      const prevCount = queryClient.getQueryData<number>(NOTIFICATION_KEYS.unreadCount);
      const nowIso = new Date().toISOString();
      queryClient.setQueriesData<NotificationListResult>(
        { queryKey: ["notifications", "list"] },
        (old) =>
          old
            ? {
                ...old,
                unreadCount: 0,
                items: old.items.map((n) => (n.readAt ? n : { ...n, readAt: nowIso })),
              }
            : old,
      );
      queryClient.setQueryData<number>(NOTIFICATION_KEYS.unreadCount, 0);
      return { prevLists, prevCount };
    },
    onError: (_e, _v, ctx) => {
      ctx?.prevLists?.forEach(([key, data]) => queryClient.setQueryData(key, data));
      if (ctx && typeof ctx.prevCount === "number") {
        queryClient.setQueryData(NOTIFICATION_KEYS.unreadCount, ctx.prevCount);
      }
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all }),
  });
}
